import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { toast } from "sonner";
import { apiClient } from "@/lib/api-client";
import { setAuthCookies } from "@/lib/auth-cookies";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ExternalLink } from "lucide-react";

const RETOOL_ACCESS_URL = 'https://joinsaturn.retool.com/apps/c3424774-b52f-11ee-94b1-0bea85df5dcf/Grant%20dashboard%20access';

// Helper to parse JWT token
function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export function GoogleLoginButton() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [showAccessLink, setShowAccessLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = async (credentialResponse: CredentialResponse) => {
    setIsLoading(true);
    setError('');
    setShowAccessLink(false);

    try {
      const token = credentialResponse.credential;
      if (!token) {
        throw new Error('No credential received');
      }

      // Parse JWT to check email
      const parsed = parseJwt(token);
      if (!parsed?.email) {
        throw new Error('Invalid token format');
      }

      // Check if email is from joinsaturn.com
      if (!parsed.email.includes('joinsaturn.com')) {
        setError('Access denied. Only @joinsaturn.com emails are allowed.');
        setIsLoading(false);
        return;
      }

      console.log('🔐 Authenticating with email:', parsed.email);

      // Send to Saturn API
      const response = await apiClient.post('/v3/auth/email/google?offline_mode=true', {
        id_token: token,
      });

      // Note: Response is now in camelCase due to automatic case conversion
      const { accessToken, refreshToken } = response.data;

      // Store tokens in cookies and localStorage (1 day session)
      setAuthCookies(accessToken, refreshToken);
      
      // Store Google profile picture if available
      if (parsed.picture) {
        localStorage.setItem('user_profile_picture', parsed.picture);
      }

      // Check if token has admin scopes
      const tokenData = parseJwt(accessToken);
      
      if (!tokenData) {
        console.error('❌ Failed to parse access token');
        setError('Authentication error: Invalid token format. Please try again.');
        setIsLoading(false);
        return;
      }

      // Scopes can be a string (space-separated) or an array
      let scopes: string = '';
      if (Array.isArray(tokenData.scopes)) {
        scopes = tokenData.scopes.join(' ');
      } else if (typeof tokenData.scopes === 'string') {
        scopes = tokenData.scopes;
      } else if (tokenData.scopes) {
        console.warn('⚠️ Unexpected scopes format:', tokenData.scopes);
        scopes = String(tokenData.scopes);
      }

      const hasAdminAccess = scopes.includes('dashboard') || 
                            scopes.includes('school_management') || 
                            scopes.includes('schedule_management');

      console.log('✅ Login successful!', {
        email: parsed.email,
        tokenDataKeys: Object.keys(tokenData),
        scopesRaw: tokenData.scopes,
        scopesParsed: scopes,
        hasAdminAccess: hasAdminAccess
      });

      if (!hasAdminAccess) {
        console.warn('⚠️ WARNING: Your account lacks admin scopes!', {
          receivedScopes: scopes,
          requiredScopes: 'dashboard, school_management, or schedule_management',
          solution: `Request access via Retool: ${RETOOL_ACCESS_URL}`
        });
        setError(`Your account (${parsed.email}) does not have admin dashboard permissions. Please request access using the link below.`);
        setShowAccessLink(true);
        setIsLoading(false);
        return;
      }

      toast.success(`Welcome, ${parsed.email}!`);
      navigate('/dashboard/map');
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Extract error message from various possible error formats
      let errorMessage = 'Authentication failed';
      const status = err?.response?.status;
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        // Handle different error response formats
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          // Handle detail field (could be string or object)
          errorMessage = typeof errorData.detail === 'string' 
            ? errorData.detail 
            : errorData.detail.message || JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string'
            ? errorData.error
            : errorData.error.message || JSON.stringify(errorData.error);
        } else {
          // Fallback: stringify the entire error data
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      // Ensure errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        errorMessage = JSON.stringify(errorMessage);
      }
      
      // For 404 or 403 errors, show access request link
      if (status === 404 || status === 403) {
        errorMessage = 'Access denied. You may not have permission to access the admin dashboard. Please request access using the link below.';
        setShowAccessLink(true);
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    setError('Google Sign-In failed. Please try again.');
    toast.error('Sign-in failed');
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <div>{error}</div>
            {showAccessLink && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2 bg-white hover:bg-gray-50 text-red-600 border-red-200"
                onClick={() => window.open(RETOOL_ACCESS_URL, '_blank')}
              >
                Dashboard Access
                <ExternalLink className="ml-2 h-3 w-3" />
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-center">
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={handleError}
          useOneTap={false}
          auto_select={false}
          shape="rectangular"
          size="large"
          theme="outline"
          text="signin_with"
          width="350"
        />
      </div>

      {isLoading && (
        <div className="text-center text-sm text-muted-foreground">
          Authenticating...
        </div>
      )}
    </div>
  );
}

